import { z } from 'zod';
/**
 * Questions a service asks before it can be booked (doc 64).
 *
 * Configuring them is optional; answering every configured one is not. The
 * answers are stored on the appointment as an ordered snapshot of question and
 * answer, so a later edit of the service — or its removal — never changes what
 * a booking says was asked.
 *
 * Pure and shared: the portal, the gateway's booking domain and tool layer, and
 * the owner-booking callable all validate through {@link resolveServiceAnswers},
 * so a booking is either complete in every writer or refused in every writer.
 */
export const MAX_SERVICE_QUESTIONS = 10;
export const MAX_SERVICE_QUESTION_LENGTH = 200;
export const MAX_SERVICE_ANSWER_LENGTH = 500;
/** Generated by the portal (UUIDs); bounded so a rule can check it. */
export const SERVICE_QUESTION_ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;
export const ServiceQuestionId = z.string().regex(SERVICE_QUESTION_ID_PATTERN);
/** One configured question. Array order is the order it is asked in. */
export const ServiceQuestionSchema = z.object({
    id: ServiceQuestionId,
    text: z.string().trim().min(1).max(MAX_SERVICE_QUESTION_LENGTH),
});
export const ServiceQuestionsSchema = z
    .array(ServiceQuestionSchema)
    .max(MAX_SERVICE_QUESTIONS)
    .superRefine((qs, ctx) => {
    const seen = new Set();
    qs.forEach((q, i) => {
        if (seen.has(q.id))
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: [i, 'id'], message: 'Duplicate question id.' });
        seen.add(q.id);
    });
});
/**
 * An answer as a model or the owner's form hands it in. The question TEXT is
 * never taken from the input: the snapshot's text comes from the service the
 * server holds. Loosely bounded here so the resolver can say exactly what is
 * wrong (an unknown id, an answer that is too long) instead of a schema error.
 */
export const ServiceAnswerInputSchema = z.object({
    questionId: z.string().min(1).max(64),
    answer: z.string().max(MAX_SERVICE_ANSWER_LENGTH * 4),
});
export const ServiceAnswerInputsSchema = z.array(ServiceAnswerInputSchema).max(MAX_SERVICE_QUESTIONS * 2);
/** The stored snapshot: what was asked, in the owner's words, and the answer. */
export const ServiceAnswerSchema = z.object({
    questionId: ServiceQuestionId,
    question: z.string().min(1).max(MAX_SERVICE_QUESTION_LENGTH),
    answer: z.string().min(1).max(MAX_SERVICE_ANSWER_LENGTH),
});
export const ServiceAnswersSchema = z.array(ServiceAnswerSchema).max(MAX_SERVICE_QUESTIONS);
/**
 * Check answers against a service's questions and build the snapshot.
 *
 * - every question needs a non-blank answer (trimmed; never truncated);
 * - an id the service does not ask, or one answered twice, is refused — a
 *   stale form's answer must not satisfy a question that has since been
 *   replaced;
 * - the snapshot follows the SERVICE's order whatever the input order, and its
 *   question text is the service's.
 *
 * A service with no questions takes only an empty list.
 */
export function resolveServiceAnswers(questions, inputs) {
    const qs = questions ?? [];
    const known = new Set(qs.map((q) => q.id));
    const given = new Map();
    const unknownIds = [];
    const duplicateIds = [];
    const tooLongIds = [];
    for (const input of inputs ?? []) {
        const id = input.questionId;
        if (!known.has(id)) {
            if (!unknownIds.includes(id))
                unknownIds.push(id);
            continue;
        }
        if (given.has(id)) {
            if (!duplicateIds.includes(id))
                duplicateIds.push(id);
            continue;
        }
        given.set(id, input.answer.trim());
    }
    const answers = [];
    const missing = [];
    for (const q of qs) {
        const answer = given.get(q.id) ?? '';
        if (answer.length === 0)
            missing.push(q);
        else if (answer.length > MAX_SERVICE_ANSWER_LENGTH)
            tooLongIds.push(q.id);
        else
            answers.push({ questionId: q.id, question: q.text, answer });
    }
    const invalid = unknownIds.length > 0 || duplicateIds.length > 0 || tooLongIds.length > 0;
    if (!invalid && missing.length === 0)
        return { ok: true, answers };
    return {
        ok: false,
        reason: invalid ? 'invalid_service_answers' : 'service_answers_required',
        missing,
        unknownIds,
        duplicateIds,
        tooLongIds,
    };
}
/** The missing questions as a tool result carries them (snake_case, bounded). */
export function missingQuestionsForTool(missing) {
    return missing.slice(0, MAX_SERVICE_QUESTIONS).map((q) => ({ question_id: q.id, question: q.text }));
}
/** Snapshots back to the input pairs they were resolved from. */
export function answerInputsOf(answers) {
    return answers.map((a) => ({ questionId: a.questionId, answer: a.answer }));
}
//# sourceMappingURL=serviceQuestions.js.map